import React, { useState, useEffect } from 'react';
import {
  Typography,
  Box,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Chip,
  CircularProgress,
  Button,
  Alert
} from '@mui/material';
import { 
  CheckCircle, 
  Error, 
  Refresh
} from '@mui/icons-material';
import axios from 'axios';

const AgentHealthMonitor = ({ workflow }) => {
  const [agentStatuses, setAgentStatuses] = useState({});
  const [loading, setLoading] = useState(false);
  const [lastCheck, setLastCheck] = useState(null);

  const checkAgentHealth = async (agentUrl) => {
    try {
      const response = await axios.get(`${agentUrl}/health`, { timeout: 5000 });
      return response.status === 200;
    } catch (error) {
      return false;
    }
  };

  const checkAllAgents = async () => {
    if (!workflow.agents || Object.keys(workflow.agents).length === 0) {
      return;
    }

    setLoading(true);
    const results = {};
    
    for (const [agentKey, agent] of Object.entries(workflow.agents)) {
      results[agentKey] = await checkAgentHealth(agent.url);
    }
    
    setAgentStatuses(results);
    setLastCheck(new Date());
    setLoading(false);
  };

  useEffect(() => {
    checkAllAgents();
    // Auto-refresh every 30 seconds
    const interval = setInterval(checkAllAgents, 30000);
    return () => clearInterval(interval);
  }, [workflow]); // eslint-disable-line react-hooks/exhaustive-deps

  if (workflow.status === 'planned') {
    return (
      <Box>
        <Typography variant="h6" sx={{ mb: 2, display: 'flex', alignItems: 'center' }}>
          🤖 Agent Status
        </Typography>
        <Alert severity="warning">
          ⚠️ This workflow is planned but not yet implemented
        </Alert>
      </Box>
    );
  }

  if (!workflow.agents || Object.keys(workflow.agents).length === 0) {
    return (
      <Box>
        <Typography variant="h6" sx={{ mb: 2, display: 'flex', alignItems: 'center' }}>
          🤖 Agent Status
        </Typography>
        <Alert severity="warning">
          ⚠️ No agents configured for this workflow
        </Alert>
      </Box>
    );
  }

  const allHealthy = Object.values(agentStatuses).every(status => status === true);
  const someUnhealthy = Object.values(agentStatuses).some(status => status === false);

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center' }}>
          🤖 Agent Status
        </Typography>
        <Button
          size="small"
          onClick={checkAllAgents}
          disabled={loading}
          startIcon={loading ? <CircularProgress size={16} /> : <Refresh />}
        >
          {loading ? 'Checking...' : 'Refresh'}
        </Button>
      </Box>

      {/* Overall Status */}
      {Object.keys(agentStatuses).length > 0 && (
        <Box sx={{ mb: 2 }}>
          <Chip
            icon={allHealthy ? <CheckCircle /> : <Error />}
            label={allHealthy ? 'All Agents Online' : someUnhealthy ? 'Some Agents Offline' : 'Checking...'}
            color={allHealthy ? 'success' : someUnhealthy ? 'error' : 'default'}
            variant="outlined"
            size="small"
          />
        </Box>
      )}

      {/* Individual Agent Status */}
      <List dense>
        {Object.entries(workflow.agents).map(([agentKey, agent]) => {
          const isHealthy = agentStatuses[agentKey];
          
          return (
            <ListItem key={agentKey} sx={{ px: 0 }}>
              <ListItemIcon sx={{ minWidth: 40 }}>
                {loading ? (
                  <CircularProgress size={20} />
                ) : isHealthy ? (
                  <CheckCircle color="success" />
                ) : (
                  <Error color="error" />
                )}
              </ListItemIcon>
              <ListItemText
                primary={
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <Typography sx={{ mr: 1 }}>{agent.icon}</Typography>
                    <Typography variant="body2">{agent.name}</Typography>
                  </Box>
                }
                secondary={
                  <Box>
                    <Typography variant="caption" color="text.secondary">
                      {agent.description}
                    </Typography>
                    <br />
                    <Typography variant="caption" color="text.secondary">
                      Port: {agent.port}
                    </Typography>
                  </Box>
                }
              />
            </ListItem>
          );
        })}
      </List>

      {lastCheck && (
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
          Last checked: {lastCheck.toLocaleTimeString()}
        </Typography>
      )}

      {someUnhealthy && (
        <Alert severity="error" sx={{ mt: 2 }}>
          Some agents are offline. Make sure to start them with:
          <br />
          <code>uv run python start_agents.py</code>
        </Alert>
      )}
    </Box>
  );
};

export default AgentHealthMonitor;